import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import moment from 'moment-timezone';
import { useState } from 'react';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryLabel,
  VictoryLabelProps,
  VictoryTheme,
  VictoryTooltip,
} from 'victory';

import type { AnalyticsEvent } from '@/analytics/events';
import useMixPanel from '@/hooks/useMixPanel';

import { theme } from '../../theme';
import { dateStringToDate, formatNumber } from '../../util';
import { Button } from '../Button';
import { ErrorBoundary } from '../ErrorBoundary';
import type { Data, Datum, Granularity, Series } from './types';
import {
  appendTotalLine,
  ESTIMATED_FILL_BASE,
  ESTIMATED_HATCH_STRIPE,
  ESTIMATED_SWATCH_BACKGROUND,
  ESTIMATED_SWATCH_BORDER,
} from './util';

/**
 * Color palette for multi-series (per-meter) bar charts. Cycled in order so the
 * "first" meter is always the same primary color and additional meters take
 * progressively distinct hues. Realistic property-management units have 1–4
 * meters; the palette is comfortable up to 8 before colors start repeating.
 */
// Ordered so the deuteranopia-hostile red/green pair is never adjacent and
// never lands together in the common 2-3 meter cases: the first three hues
// (blue, orange, purple) stay distinguishable under red-green color blindness.
const MULTI_SERIES_PALETTE = [
  theme.palette.primary.main,
  '#FFB74D', // orange
  '#9575CD', // purple
  '#E57373', // red
  '#4DB6AC', // teal
  '#F06292', // pink
  '#81C784', // green
  '#A1887F', // brown
];

const colorForSeries = (series: Series, index: number): string =>
  series.color ?? MULTI_SERIES_PALETTE[index % MULTI_SERIES_PALETTE.length];

/**
 * Estimated usage renders with a neutral-gray diagonal hatch (W2 design, revised):
 * texture is the discriminator, not hue, so it survives color blindness AND cannot
 * be confused with any meter color from MULTI_SERIES_PALETTE in per-meter views.
 * The pattern lives in a zero-size sibling SVG; url(#id) references resolve
 * document-wide, which is the standard way to share defs with Victory's SVG.
 */
const ESTIMATED_HATCH_ID = 'ssw-estimated-usage-hatch';

const EstimatedHatchDefs = (): JSX.Element => (
  <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden focusable="false">
    <defs>
      <pattern
        id={ESTIMATED_HATCH_ID}
        patternUnits="userSpaceOnUse"
        width={5}
        height={5}
        patternTransform="rotate(45)"
      >
        {/* Opaque base so gridlines don't show through the segment */}
        <rect width={5} height={5} fill={ESTIMATED_FILL_BASE} />
        <line x1={0} y1={0} x2={0} y2={5} stroke={ESTIMATED_HATCH_STRIPE} strokeWidth={1.6} />
      </pattern>
    </defs>
  </svg>
);

type Props = {
  createCSV?: (arg0: Data) => Promise<string>;
  csvTitle?: string;
  eventType?: AnalyticsEvent;
  eventData?: {};
  data: Data;
  title?: VictoryLabelProps['text'];
  x: string;
  y: string;
  style?: {};
  yAxisType?: string;
  /**
   * 'daily' (default) renders one bar per data point with date-formatted ticks.
   * 'hourly' switches the x-axis to a time scale and renders smart tick labels
   * whose density adapts to the range. Each datum must include `xValue`
   * (epoch_ms) when `granularity === 'hourly'`.
   */
  granularity?: Granularity;
  /**
   * IANA timezone (e.g. "America/New_York") to render hourly tick + tooltip labels in.
   * Falls back to the browser's local timezone if omitted. Ignored for daily mode
   * since daily date strings are already pre-formatted in the property's timezone
   * by the backend.
   */
  timezone?: string;
  /**
   * [startEpochMs, endEpochMs] — explicit x-axis domain for hourly mode. When provided,
   * the chart axis spans this full range regardless of where the data starts/ends, so
   * gaps in device reporting render as empty space instead of being truncated. Ignored
   * when granularity is 'daily'.
   */
  xDomain?: [number, number];
  /**
   * Multi-series mode (per-meter view on multi-meter units). When provided AND
   * non-empty, the chart renders a stacked bar chart with one segment per series
   * and a legend underneath. The single-series `data` prop is ignored in this
   * mode for the bar rendering, but is still used as the source for the CSV
   * export (callers should pass the combined dataset for export consistency).
   */
  series?: Series[];
  /**
   * Estimated usage overlay (support-entered estimates for periods where meter
   * data is unavailable). Same Datum shape as `data`, pre-aggregated per bucket
   * by buildEstimatedChartData. Rendered as a gray hatched segment
   * segment stacked ABOVE the real usage at the same x, per the W2 design
   * decision (see DESIGN-estimated-usage-chart.md): the distinction is fill
   * lightness + outline + legend text, never hue alone, so it survives color
   * blindness and monochrome print.
   */
  estimated?: Data;
  /**
   * Pass false to disable the entrance animation (tests assert on rendered SVG
   * geometry, which is collapsed on the animation's first frame).
   */
  animate?: boolean;
};

/** Pick a tick label format that fits the visible range without overlapping. */
const pickHourlyTickFormat = (rangeMs: number): string => {
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  if (rangeMs <= 2 * ONE_DAY_MS) return 'h A';
  if (rangeMs <= 7 * ONE_DAY_MS) return 'M/D h A';
  return 'M/D';
};

export const Chart = (props: Props): JSX.Element => {
  const granularity: Granularity = props.granularity ?? 'daily';
  const isHourly = granularity === 'hourly';
  const tz = props.timezone || moment.tz.guess();

  // Multi-series rendering kicks in when the caller passes 2+ series. (One series
  // is treated as single-series for visual consistency, since there's no point stacking one bar
  // and rendering a one-item legend.)
  const isMulti = (props.series?.length ?? 0) >= 2;

  // Click-to-isolate behavior in the legend: clicking a meter name renders only
  // that meter's bars. Clicking the same name again (or clicking elsewhere)
  // returns to the all-series view. State is local to this Chart instance, so
  // navigating away or remounting resets it.
  const [isolatedSeriesName, setIsolatedSeriesName] = useState<string | null>(null);

  // Build the visible-series list. Even when isolating, we keep the legend showing
  // every series (so the user can re-select another one without first resetting),
  // and we keep the x-axis sized to the union of ALL series so isolating doesn't
  // shrink the chart's date range. Only the bar rendering filters down.
  const seriesWithIndex = (props.series ?? []).map((s, originalIndex) => ({
    series: s,
    originalIndex,
  }));
  const visibleSeries = isolatedSeriesName
    ? seriesWithIndex.filter((item) => item.series.name === isolatedSeriesName)
    : seriesWithIndex;

  // Reference dataset for sizing/axis derivations. In multi-series mode we build the
  // union of x-values across ALL series, sorted, so the daily x-axis tick formatter
  // produces one label per distinct date Victory will actually render. Using only
  // series[0] as the reference broke when a later series had more entries (or
  // different dates), because Victory's stacked bar internally spans the union and
  // would then look up labels off the end of a too-short tickFormat array, landing
  // on `undefined` and crashing inside its label-overlap calculation.
  //
  // The dedup key has to match the granularity: in daily mode dedupe by `date`
  // (YYYY-MM-DD), in hourly mode dedupe by `xValue` (epoch_ms). Deduping hourly
  // multi-series data by `date` collapses ~72 hourly buckets into ~3 entries,
  // which makes `domainPadding` below blow up to ~37px and shifts the per-meter
  // chart's time axis off from the combined chart even when both receive the
  // same `xDomain`, so the bars stop lining up with the top chart.
  // Estimated overlay is hidden while a meter is isolated: isolation is a per-meter
  // drill-down of REAL meter data, and estimates are unit-level (they exist precisely
  // because a meter was offline or absent, so attributing them to one meter's view
  // would be misleading). The legend keeps the Estimated chip visible so the user
  // knows estimates exist for the range.
  const estimatedData: Data = props.estimated ?? [];
  const showEstimated = estimatedData.length > 0 && isolatedSeriesName === null;

  const xKeyOf = (d: Datum): string => (isHourly ? String(d.xValue) : d.date);

  const referenceData: Data = (() => {
    // Include estimated-only buckets in the reference set so a day that has ONLY
    // estimated usage (e.g. a unit with no devices) still gets an x-axis tick.
    const base: Data = isMulti ? props.series!.flatMap((s) => s.data) : props.data;
    const combined = showEstimated ? [...base, ...estimatedData] : base;
    if (!isMulti && !showEstimated) return props.data;
    return Array.from(new Map(combined.map((d) => [xKeyOf(d), d])).values()).sort((a, b) =>
      isHourly ? (a.xValue ?? 0) - (b.xValue ?? 0) : a.date < b.date ? -1 : 1,
    );
  })();

  const domainPadding = referenceData.length <= 40 ? 40 - referenceData.length : 10;
  const { yAxisType = 'gal' } = props;
  const { trackEvent } = useMixPanel();

  // Hourly: derive the visible range from data so the tick formatter can pick density.
  // In multi-series mode, walk every series since each meter may have its own coverage.
  const hourlyXValues = isHourly
    ? (isMulti
        ? props.series!.flatMap((s) => s.data.map((d) => d.xValue))
        : props.data.map((d) => d.xValue)
      ).filter((v): v is number => typeof v === 'number')
    : [];

  // Compute a Y-axis domain from the actual data so hourly charts (with small
  // per-bucket values) don't get squashed into the bottom of the chart, and
  // daily charts with large values aren't clipped at a hardcoded ceiling.
  //
  // For multi-series we use the *visible* series (post-isolation), not the full
  // set: when a user clicks a low-usage meter to isolate it, the axis should
  // tighten to that meter's range instead of leaving headroom for the absent
  // segments. Computed against max-of-sums-per-x (the visible bar height is the
  // sum of segments), not max-of-individual-segments.
  // Estimated volume per x, folded into the bar-height calculation below (the
  // visible bar height is real + estimated when the overlay is shown) and used
  // as the stacking base lookup for the overlay's y0.
  const estimatedByX = new Map<string, number>();
  if (showEstimated) {
    estimatedData.forEach((d) => {
      estimatedByX.set(xKeyOf(d), (estimatedByX.get(xKeyOf(d)) ?? 0) + (d.volume ?? 0));
    });
  }

  // Real (metered) volume per x. The estimated overlay stacks on top of this
  // (its y0), and the y-domain sizes to real + estimated.
  const realTotalByX = (() => {
    const sumByX = new Map<string, number>();
    if (isMulti) {
      visibleSeries.forEach(({ series: s }) => {
        s.data.forEach((d) => {
          const key = xKeyOf(d);
          sumByX.set(key, (sumByX.get(key) ?? 0) + (d.volume ?? 0));
        });
      });
    } else {
      props.data.forEach((d) => {
        sumByX.set(xKeyOf(d), (sumByX.get(xKeyOf(d)) ?? 0) + (d.volume ?? 0));
      });
    }
    return sumByX;
  })();

  const maxY = (() => {
    const sumByX = new Map<string, number>(realTotalByX);
    estimatedByX.forEach((vol, key) => {
      sumByX.set(key, (sumByX.get(key) ?? 0) + vol);
    });
    // Array.from() so we don't need TypeScript's --downlevelIteration flag.
    return sumByX.size > 0 ? Math.max(...Array.from(sumByX.values())) : 0;
  })();
  // 10% headroom over the tallest bar (so the top isn't flush against the chart
  // edge), then ceil so the top tick lands on a whole number. Falls back to a
  // sensible default when there's no data so the empty state still has a sane axis.
  const yDomain: [number, number] = maxY > 0 ? [0, Math.ceil(maxY * 1.1)] : [0, 10];
  const hourlyRangeMs =
    hourlyXValues.length > 1 ? Math.max(...hourlyXValues) - Math.min(...hourlyXValues) : 0;
  const hourlyTickFormatStr = pickHourlyTickFormat(hourlyRangeMs);

  // Day-boundary gridlines (midnight in property tz) to anchor the eye when the hourly
  // view spans multiple days. Computed off xDomain when provided (so the boundaries match
  // the requested range, not just where data exists).
  const dayBoundaryTickValues: number[] = (() => {
    if (!isHourly) return [];
    const startMs =
      props.xDomain?.[0] ?? (hourlyXValues.length ? Math.min(...hourlyXValues) : null);
    const endMs = props.xDomain?.[1] ?? (hourlyXValues.length ? Math.max(...hourlyXValues) : null);
    if (startMs == null || endMs == null) return [];
    const ticks: number[] = [];
    let cursor = moment.tz(startMs, tz).startOf('day');
    if (cursor.valueOf() < startMs) cursor.add(1, 'day');
    while (cursor.valueOf() <= endMs) {
      ticks.push(cursor.valueOf());
      cursor = cursor.add(1, 'day');
    }
    return ticks;
  })();

  // Tooltip text comes from `datum.label` (which callers bake — daily callers use
  // createChartLabel; hourly callers bake a time-aware label in the property timezone).
  // Avoiding Victory's `labels` prop callback because its signature varies across versions
  // and silently falling back to datum.label produced wrong (date-only) tooltips.

  // Key forces a full VictoryChart remount whenever granularity flips or the
  // underlying data shape changes. The previous version derived this only from
  // `props.data` and silently fell back to a static key when `data` was empty
  // (which is the case in multi-series mode), so the second chart kept stale
  // bars after a Daily → Hourly → Daily round-trip. Now we look at whichever
  // dataset actually drives the render: `series[0].data` for multi, `data`
  // for single, so the key updates in both modes.
  const chartKey = (() => {
    const refDatum = isMulti ? props.series?.[0]?.data?.[0] : props.data[0];
    if (!refDatum) return `${granularity}-${isMulti ? 'multi' : 'single'}-empty`;
    const fingerprint = isHourly ? refDatum.xValue : refDatum.date;
    return `${granularity}-${isMulti ? 'multi' : 'single'}-${fingerprint}`;
  })();

  return (
    <ErrorBoundary>
      {(props.estimated?.length ?? 0) > 0 && <EstimatedHatchDefs />}
      <VictoryChart
        height={450}
        width={700}
        // Lock the plot area's left/right padding so the Y-axis label region is
        // identical between any two Charts, regardless of label widths. Without
        // this, a chart with "12 gal" labels has slightly different left padding
        // than a chart with "1.5 gal" labels, and bars at the same x-value end
        // up at different x-pixels, so the top combined chart and the bottom
        // per-meter chart appear misaligned even when their x-values match.
        padding={{ top: 30, bottom: 50, left: 60, right: 30 }}
        domainPadding={domainPadding}
        theme={VictoryTheme.material}
        animate={
          props.animate === false ? undefined : { duration: 2000, onLoad: { duration: 1000 } }
        }
        scale={isHourly ? { x: 'time' } : undefined}
        domain={isHourly && props.xDomain ? { x: props.xDomain } : undefined}
        containerComponent={
          <VictoryContainer responsive={false} style={{ margin: '0 auto', ...props.style }} />
        }
        key={chartKey}
      >
        {props.title && (
          <VictoryLabel
            text={props.title}
            x={165}
            y={10}
            dy={20}
            textAnchor="middle"
            style={{ fontSize: 14 }}
          />
        )}
        {/* Y-axis */}
        <VictoryAxis
          dependentAxis
          domain={yDomain}
          tickFormat={(x) => `${formatNumber(x)} ${yAxisType}`}
          style={{
            tickLabels: { fontSize: 12 },
          }}
        />
        {isMulti ? (
          // Multi-series: render one VictoryBar per meter, each with a manually
          // computed `y0` so they visually stack. We deliberately do NOT use
          // <VictoryStack> here; it derives its own x-coordinate system that
          // disagrees with the parent VictoryChart's xDomain + domainPadding,
          // pushing bars inward and adding phantom leading ticks (the misaligned-
          // hourly bug). Bare VictoryBars inherit the parent's domain directly,
          // exactly the way the single-series combined chart does, so the
          // positioning is guaranteed identical between the two charts.
          //
          // We return the bars as an array (NOT a React.Fragment) because
          // VictoryChart parses its children at runtime to identify charts/bars/
          // axes; a fragment in the middle of children hides the bars from that
          // parser and the chart renders empty. Returning a top-level array
          // keeps the children flat from VictoryChart's perspective.
          //
          // y0 = sum of `volume` from all previous series at this same x value;
          // yTop = y0 + this series's volume. Bar renders between y0 (absolute
          // bottom) and yTop (absolute top). Visually equivalent to a stack.
          visibleSeries.map(({ series, originalIndex }, sIdx) => {
            const fill = colorForSeries(series, originalIndex);
            const stackedData = series.data.map((d) => {
              const xKey = isHourly ? String(d.xValue) : d.date;
              let y0 = 0;
              for (let i = 0; i < sIdx; i++) {
                const prevSeries = visibleSeries[i].series;
                const prevDatum = prevSeries.data.find(
                  (p) => (isHourly ? String(p.xValue) : p.date) === xKey,
                );
                if (prevDatum) y0 += prevDatum.volume ?? 0;
              }
              return {
                ...d,
                y0,
                yTop: y0 + (d.volume ?? 0),
                // Override the per-bar tooltip label so it includes the meter name.
                // We deliberately don't repeat a rounded volume in the prefix; the
                // datum's existing `label` already carries the precise per-bucket
                // value (e.g. "1.23 gal at 3/2/2026 9:00 AM"), and showing a
                // separate rounded copy above it read as misleading on hover.
                label: appendTotalLine(
                  `${series.name}\n${d.label}`,
                  realTotalByX.get(xKeyOf(d)) ?? 0,
                  estimatedByX.get(xKeyOf(d)) ?? 0,
                  isHourly ? 'hourly' : 'daily',
                ),
              };
            });
            return (
              <VictoryBar
                key={series.name}
                x={isHourly ? 'xValue' : props.x}
                y="yTop"
                y0="y0"
                data={stackedData}
                barRatio={isHourly ? 1 : undefined}
                style={{ data: { fill } }}
                labelComponent={
                  <VictoryTooltip
                    style={{
                      strokeWidth: 1,
                      fontSize: 12,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  />
                }
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onMouseOver: () => [
                        {
                          target: 'data',
                          mutation: () => ({ style: { fill, fillOpacity: 0.7 } }),
                        },
                        { target: 'labels', mutation: () => ({ active: true }) },
                      ],
                      onMouseOut: () => [
                        { target: 'data', mutation: () => null },
                        { target: 'labels', mutation: () => ({ active: false }) },
                      ],
                    },
                  },
                ]}
              />
            );
          })
        ) : (
          <VictoryBar
            x={isHourly ? 'xValue' : props.x}
            y={props.y}
            data={props.data.map((d) => ({
              ...d,
              label: appendTotalLine(
                (d as { label?: string }).label ?? '',
                realTotalByX.get(xKeyOf(d)) ?? 0,
                estimatedByX.get(xKeyOf(d)) ?? 0,
                isHourly ? 'hourly' : 'daily',
              ),
            }))}
            // Histogram look in hourly mode: bars fill their full hour-slot with no gap, so the
            // chart reads as continuous binned data. At narrow ranges (~24 bars / day) bars are
            // chunky; at wide ranges (~720 bars / 30 days) bars are ~1px and visually approach
            // an area chart. Daily mode keeps Victory's default bar/gap behavior.
            barRatio={isHourly ? 1 : undefined}
            // Hourly bars use primary.dark to visually distinguish from the daily mode fill,
            // making it obvious at a glance which mode is active.
            style={{
              data: { fill: isHourly ? theme.palette.primary.dark : theme.palette.primary.main },
            }}
            labelComponent={
              <VictoryTooltip
                style={{
                  strokeWidth: 1,
                  fontSize: 12,
                  fontFamily: theme.typography.fontFamily,
                }}
              />
            }
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onMouseOver: () => [
                    {
                      target: 'data',
                      mutation: () => ({
                        style: {
                          fill: isHourly ? theme.palette.primary.main : theme.palette.primary.dark,
                        },
                      }),
                    },
                    { target: 'labels', mutation: () => ({ active: true }) },
                  ],
                  onMouseOut: () => [
                    { target: 'data', mutation: () => null },
                    { target: 'labels', mutation: () => ({ active: false }) },
                  ],
                },
              },
            ]}
          />
        )}
        {showEstimated && (
          // Estimated usage overlay: hollow segment stacked above the real usage at
          // the same x (y0 = real total for the bucket). Hollow fill + outline is
          // the W2 treatment: distinguishable without color vision and in print.
          //
          // The series is DENSIFIED to one datum per reference bucket (zero-volume
          // buckets render fully transparent) so Victory computes the same default
          // bar width as the real series. A sparse series here (points only where
          // estimates exist) made Victory derive its width from that tiny point
          // count, drawing giant bars that spilled across the chart.
          <VictoryBar
            x={isHourly ? 'xValue' : props.x}
            y="yTop"
            y0="y0"
            data={referenceData.map((refDatum) => {
              const xKey = xKeyOf(refDatum);
              const estimatedVolume = estimatedByX.get(xKey) ?? 0;
              const realTotal = realTotalByX.get(xKey) ?? 0;
              const estimatedDatum = estimatedData.find((d) => xKeyOf(d) === xKey);
              return {
                ...refDatum,
                volume: estimatedVolume,
                y0: realTotal,
                yTop: realTotal + estimatedVolume,
                label: appendTotalLine(
                  estimatedDatum?.label ?? '',
                  realTotal,
                  estimatedVolume,
                  isHourly ? 'hourly' : 'daily',
                ),
              };
            })}
            barRatio={isHourly ? 1 : undefined}
            style={{
              data: {
                fill: ({ datum }) =>
                  (datum.volume ?? 0) > 0 ? `url(#${ESTIMATED_HATCH_ID})` : 'transparent',
              },
            }}
            labelComponent={
              <VictoryTooltip
                style={{
                  strokeWidth: 1,
                  fontSize: 12,
                  fontFamily: theme.typography.fontFamily,
                }}
              />
            }
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onMouseOver: () => [
                    {
                      target: 'data',
                      mutation: (mutationProps) =>
                        (mutationProps.datum?.volume ?? 0) > 0
                          ? {
                              style: {
                                fill: `url(#${ESTIMATED_HATCH_ID})`,
                                fillOpacity: 0.7,
                              },
                            }
                          : null,
                    },
                    { target: 'labels', mutation: () => ({ active: true }) },
                  ],
                  onMouseOut: () => [
                    { target: 'data', mutation: () => null },
                    { target: 'labels', mutation: () => ({ active: false }) },
                  ],
                },
              },
            ]}
          />
        )}
        {/* Hourly: faint vertical gridlines at midnight in the property timezone (overlay axis,
            no labels) so the user can see day boundaries inside the data range. */}
        {isHourly && dayBoundaryTickValues.length > 0 && (
          <VictoryAxis
            tickValues={dayBoundaryTickValues}
            style={{
              axis: { stroke: 'transparent' },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
              grid: {
                stroke: theme.palette.grey[400],
                strokeDasharray: '4,4',
                strokeWidth: 1,
              },
            }}
          />
        )}
        {/* X-axis */}
        {isHourly ? (
          <VictoryAxis
            fixLabelOverlap={true}
            tickCount={10}
            tickFormat={(t: number | Date) => {
              const ms = t instanceof Date ? t.getTime() : t;
              return moment.tz(ms, tz).format(hourlyTickFormatStr);
            }}
            gridComponent={<span />}
            style={{
              tickLabels: { fontSize: 12 },
            }}
          />
        ) : (
          <VictoryAxis
            fixLabelOverlap={true}
            gridComponent={<span />}
            style={{
              tickLabels: { fontSize: 12 },
            }}
            tickValues={referenceData.map((_, idx) => idx + 1)}
            // Function tickFormat (vs an array) so any tick index Victory asks for
            // beyond referenceData.length resolves to '' instead of undefined, which
            // protected against the crash where Victory's `fixLabelOverlap` calls
            // `.toString()` on undefined labels during transient data-change states.
            tickFormat={(_t: number, idx: number) => {
              const datum = referenceData[idx];
              if (!datum?.date) return '';
              try {
                return format(dateStringToDate(datum.date), 'MMM do');
              } catch {
                return '';
              }
            }}
          />
        )}
      </VictoryChart>
      {isMulti && (
        // Legend strip below the chart. Built with MUI primitives (rather than
        // VictoryLegend) so we get free wrapping for many-meter cases and don't
        // have to compute pixel positions inside the SVG.
        //
        // Each item is styled as a chip (border + rounded corners + hover fill)
        // to clearly communicate clickability. Click any chip to isolate that
        // meter (chart filters to just that series); click the same chip again
        // (or any other chip) to switch isolation. The active chip gets a
        // colored border + tinted background so the current selection is
        // unambiguous; hidden (non-isolated) chips dim to 40% so it's clear
        // they're not currently visible in the chart but are still toggleable.
        <Box sx={{ width: 700, margin: 'auto', mt: 1, px: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              mb: 0.75,
              fontStyle: 'italic',
            }}
          >
            Click a meter to filter the chart
          </Typography>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
            {seriesWithIndex.map(({ series, originalIndex }) => {
              const isActive = isolatedSeriesName === series.name;
              const isHidden = isolatedSeriesName !== null && !isActive;
              const swatchColor = colorForSeries(series, originalIndex);
              return (
                <Stack
                  key={series.name}
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  onClick={() =>
                    setIsolatedSeriesName((prev) => (prev === series.name ? null : series.name))
                  }
                  sx={{
                    cursor: 'pointer',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isActive ? theme.palette.primary.main : theme.palette.grey[300],
                    backgroundColor: isActive
                      ? `${theme.palette.primary.main}14` // ~8% tint
                      : 'transparent',
                    opacity: isHidden ? 0.4 : 1,
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: isActive
                        ? `${theme.palette.primary.main}24` // ~14% tint
                        : theme.palette.grey[100],
                      borderColor: isActive ? theme.palette.primary.dark : theme.palette.grey[400],
                      opacity: isHidden ? 0.7 : 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      backgroundColor: swatchColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    {series.name}
                  </Typography>
                </Stack>
              );
            })}
            {(props.estimated?.length ?? 0) > 0 && (
              // Estimated legend entry (hollow swatch, per W2). Not clickable: the
              // overlay is unit-level, so it has no per-meter isolation behavior.
              // It stays visible while a meter is isolated as a cue that estimates
              // exist for this range even though the overlay itself is hidden.
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: theme.palette.grey[400],
                  opacity: isolatedSeriesName !== null ? 0.4 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    border: `1px solid ${ESTIMATED_SWATCH_BORDER}`,
                    background: ESTIMATED_SWATCH_BACKGROUND,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: 12 }}>
                  Estimated
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      )}
      {!isMulti && (props.estimated?.length ?? 0) > 0 && (
        // Single-series mode has no legend normally; when estimates are present we
        // add a minimal one so the hollow segment is self-explanatory (W2: color is
        // never the only signal, the legend carries the word "Estimated").
        <Box sx={{ width: 700, margin: 'auto', mt: 1, px: 2 }}>
          <Stack direction="row" justifyContent="center" gap={2}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '2px',
                  backgroundColor: isHourly
                    ? theme.palette.primary.dark
                    : theme.palette.primary.main,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 12 }}>
                Metered
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '2px',
                  border: `1px solid ${ESTIMATED_SWATCH_BORDER}`,
                  background: ESTIMATED_SWATCH_BACKGROUND,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 12 }}>
                Estimated
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}
      {props.createCSV && (
        <Box sx={{ width: 700, margin: 'auto', textAlign: 'end' }}>
          <Button
            sx={{ marginRight: '48px' }}
            onClick={() => {
              props.createCSV!(props.data)
                .then((csv) =>
                  saveAs(
                    new Blob([csv], { type: 'text/plain;charset=utf-8' }),
                    `${(props.csvTitle || 'csv-download').replace(/ /g, '_')}.csv`,
                  ),
                )
                // createCSV may reject when data isn't ready (the caller surfaces a toast in
                // that case). Swallow the rejection so it doesn't propagate as an uncaught
                // runtime error in dev tools.
                .catch(() => {
                  /* no-op */
                });
              if (props.eventType) {
                trackEvent(props.eventType, props.eventData);
              }
            }}
          >
            Export to Spreadsheet
          </Button>
        </Box>
      )}
    </ErrorBoundary>
  );
};
